import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState } from "react";
import {
  GeoJSON,
  LayersControl,
  MapContainer,
  TileLayer,
  useMap,
} from "react-leaflet";
import CustomScaleControl from "../../../../general/CustomScaleControl";
import styles from "./LeafletMap.module.css";

// 🔧 Fly para centroide clicado com menos zoom
function ZoomToClickedCentroide({ feature }) {
  const map = useMap();

  useEffect(() => {
    if (feature) {
      const [lng, lat] = feature.geometry.coordinates;
      const latlng = L.latLng(lat, lng);
      map.flyTo(latlng, 12, {
        animate: true,
        duration: 1.5,
        easeLinearity: 0.25,
      });
    }
  }, [feature, map]);

  return null;
}

// 🔧 FitBounds para distritos selecionados (usado apenas se não clicou no centroide)
function FitBoundsControl({ geoData, distrito }) {
  const map = useMap();

  useEffect(() => {
    if (!geoData || !map) return;

    if (distrito && distrito !== "Todos") {
      const selectedFeature = geoData.features.find(
        (feature) => feature.properties.distrito === distrito
      );

      if (selectedFeature) {
        const layer = L.geoJSON(selectedFeature);
        map.fitBounds(layer.getBounds(), {
          padding: [100, 100],
          maxZoom: 12,
        });
      }
    } else {
      const geojsonLayer = L.geoJSON(geoData);
      map.fitBounds(geojsonLayer.getBounds(), {
        padding: [50, 50],
      });
    }
  }, [geoData, map, distrito]);

  return null;
}

function LeafletMap({ distrito, onDistrictMapChange }) {
  const [districtData, setDistrictData] = useState(null);
  const [centroidesData, setCentroidesData] = useState(null);
  const [clickedCentroide, setClickedCentroide] = useState(null);
  const layersRef = useRef({});

  // Limpa o centroide clicado ao trocar o distrito manualmente
  useEffect(() => {
    setClickedCentroide(null);
  }, [distrito]);

  // Carrega arquivos GeoJSON
  useEffect(() => {
    Promise.all([
      fetch("/distritos.geojson").then((res) => res.json()),
      fetch("/centroides.geojson").then((res) => res.json()),
    ])
      .then(([districts, centroides]) => {
        setDistrictData(districts);
        setCentroidesData(centroides);
      })
      .catch((err) => console.error("Erro ao carregar GeoJSONs:", err));
  }, []);

  const onEachFeature = (feature, layer) => {
    const nomeDistrito = feature.properties.distrito || "Distrito";
    layersRef.current[nomeDistrito] = layer;

    layer.on({
      click: () => {
        if (typeof onDistrictMapChange === "function") {
          onDistrictMapChange(nomeDistrito);
        }
      },
    });
  };

  return (
    <div className={styles.leafletMap}>
      <MapContainer
        center={[-20.4, -43.5]}
        zoom={13}
        style={{ width: "100%", height: "100%" }}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Open Street Map">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Carto BD">
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Esri Imagery/Satellite">
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Esri Streets">
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Esri Topo">
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Google Maps">
            <TileLayer url="http://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Google Satellite">
            <TileLayer url="http://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Google Satellite Hybrid">
            <TileLayer url="http://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="OSM Cycle Map">
            <TileLayer url="https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=aab02ccbf1c34b07872e7e8dbfb5946a" />
          </LayersControl.BaseLayer>
        </LayersControl>

        <CustomScaleControl />

        {/* Distritos - contorno */}
        {districtData && (
          <GeoJSON
            data={districtData}
            style={() => ({
              color: "black",
              weight: 2,
              dashArray: "6,6",
              fillColor: "transparent",
              fillOpacity: 0,
            })}
          />
        )}

        {/* Centroide + zoom inteligente */}
        {centroidesData && (
          <>
            <GeoJSON
              data={centroidesData}
              onEachFeature={onEachFeature}
              pointToLayer={(feature, latlng) => {
                const marker = L.circleMarker(latlng, {
                  radius: feature.properties.area_km2 / 10,
                  fillColor: "#AF8500",
                  color: "white",
                  weight: 1,
                  fillOpacity: 0.8,
                });

                marker.on("click", () => {
                  const nome = feature.properties.distrito || "Distrito";
                  setClickedCentroide(feature); // ativa flyTo
                  if (typeof onDistrictMapChange === "function") {
                    onDistrictMapChange(nome);
                  }
                });

                return marker;
              }}
            />

            {/* Somente faz fitBounds se não clicou em centroide */}
            {!clickedCentroide && (
              <FitBoundsControl geoData={centroidesData} distrito={distrito} />
            )}

            {/* Faz flyTo se clicou em centroide */}
            {clickedCentroide && (
              <ZoomToClickedCentroide feature={clickedCentroide} />
            )}
          </>
        )}
      </MapContainer>
    </div>
  );
}

export default LeafletMap;
